/** @jsx jsx */
import * as React from 'react';
import { isInteger } from 'lodash';
import { jsx } from '@emotion/core';
import {
  PropsQuery,
  PropsQueriesType,
} from '../PropsQuery';

type ItemsLayout = 'start' | 'end' | 'center' | 'stretch';

export type GridLayoutProps = {
  /**
   * How to align the items within the grid layout.
   * */
  alignItems?: ItemsLayout;
  /**
   * The grid area to render within. Only define this if the `GridLayout` is a subgrid living within a template area defined by a parent grid.
   * */
  area?: string;
  /**
   * Named grid template areas within this grid. Accepts any valid value for `grid-template-areas`. **Remember that each grid row needs to be quoted!**
   * */
  areas?: string;
  /**
   * The content rendered inside which will become grid columns and rows.
   * */
  children: React.ReactNode;
  /**
   * Number of columns or explicit column sizes. Accepts any valid value for `grid-template-columns`.
   * */
  columns?: number | string;
  /**
   * The space between rows and columns. Accepts any valid value for `grid-gap` / `gap`. If you want to specify `row-gap` or `column-gap`, use the CSS shorthand form of `gap`, which is `row-gap` followed by `column-gap`.
   *
   * @default 'normal'
   * */
  gap?: number | string;
  /**
   * How to justify the items within the grid layout.
   * */
  justifyItems?: ItemsLayout;
  /**
   * Props Queries
   * */
  propsQueries?: PropsQueriesType;
  /**
   * Number of rows or explicit row sizes. Accepts any valid value for `grid-template-rows`.
   * */
  rows?: number | string;
};

const gridLayoutCss = (props: GridLayoutProps) => ({
  alignItems: props.alignItems,
  display: 'grid',
  gridArea: props.area,
  gridGap: props.gap || 'normal',
  gridTemplateAreas: props.areas,
  gridTemplateColumns:
    props.columns && isInteger(props.columns)
      ? `repeat(${props.columns}, 1fr)`
      : props.columns,
  gridTemplateRows:
    props.rows && isInteger(props.rows)
      ? `repeat(${props.rows}, 1fr)`
      : props.rows,
  justifyItems: props.justifyItems,
});

const GridLayout = (props: GridLayoutProps) => {
  return (
    <PropsQuery queries={props.propsQueries}>
      {overrides => {
        const config = { ...props, ...overrides };
        return (
          <div css={gridLayoutCss(config)}>
            {typeof props.children === 'function'
              ? props.children(config)
              : props.children}
          </div>
        );
      }}
    </PropsQuery>
  );
};

export default GridLayout;
